import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, Loader2, Download } from 'lucide-react';
import { getTrustScoreBySlug, downloadTrustScorePdf } from '../services/api';
import { publishedBandStyle } from '../services/trustBands';
import {
  Dial, FactorRow, DeductionRow, SectionLabel, Pill,
  PANEL_STYLE, factorName, evidenceLabel,
} from '../components/v2/trust/trustScoreParts';

/**
 * A Trust Score at the address on a badge.
 *
 * Reached by following a link from somewhere else — a vendor's security page, an RFP response, a
 * questionnaire. Everything about this page assumes the reader arrived with no context: they may
 * never have seen this product, and the agent is somebody else's.
 *
 * Signing in happens before they get here, so the page itself is ordinary. What differs is what the
 * server returns: the owner's view for their own score, and what the owner chose to publish for
 * anyone else.
 */
export function TrustScoreAddressPage() {
  const { slug } = useParams();
  const [score, setScore] = useState(null);
  const [state, setState] = useState('loading');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    const res = await getTrustScoreBySlug(slug);
    if (res?.ok === false) { setState(res?.status === 404 ? 'none' : 'error'); return; }
    setScore(res?.data || res);
    setState('ready');
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  /* The file a customer sends to a reviewer who is not going to be given an account.
   *
   * It is the same scorecard this page shows and it comes from the same tenant-scoped endpoint —
   * nothing here is assembled in the browser, so what gets shared cannot drift from what gets
   * displayed. It carries no part of the input record: no subject reference, no reachable hosts,
   * no egress destinations. */
  const exportPdf = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    const r = await downloadTrustScorePdf({ slug });
    if (r?.ok === false) setExportError(r.error || 'Could not prepare that file. Try again in a moment.');
    setExporting(false);
  }, [slug]);

  const style = useMemo(() => publishedBandStyle(score?.score), [score?.score]);
  /* The scorecard leads with the heaviest few, which is what DesignTimeScoringService.deductions
   * says it is ordered for. But the full list accounts for the entire distance from a hundred, so
   * showing three of four silently leaves the arithmetic short - and TrustScoreDeduction's own
   * doc warns that a ladder which does not add up invites the reader to conclude there is a
   * hidden term. The count says which it is. */
  const allDeductions = Array.isArray(score?.deductions) ? score.deductions : [];
  const deductions = allDeductions.slice(0, 3);
  const deductionLabel = allDeductions.length > deductions.length
    ? `What lowered this score \u00b7 top ${deductions.length} of ${allDeductions.length}`
    : 'What lowered this score';
  const factors = Array.isArray(score?.factors) ? score.factors : [];

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[860px] mx-auto">
      <header className="flex items-center gap-2 mb-4">
        <ShieldCheck size={18} style={{ color: 'var(--v2-text-muted)' }} />
        <h1 className="text-[20px] font-bold tracking-tight m-0">Trust Score</h1>
        {state === 'ready' && (
          <button
            type="button"
            onClick={exportPdf}
            disabled={exporting}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-v2-sm text-[12.5px]
                       font-medium border border-v2-border-subtle text-v2-ink-secondary
                       hover:text-v2-ink hover:bg-v2-surface-muted disabled:opacity-60"
          >
            <Download size={13} />
            {exporting ? 'Preparing\u2026' : 'Download PDF'}
          </button>
        )}
      </header>

      {exportError && (
        <div className="mb-3 text-[12.5px]" style={{ color: 'var(--v2-danger)' }}>{exportError}</div>
      )}

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-[13px] text-v2-ink-muted">
          <Loader2 size={14} className="animate-spin" /> Loading this score…
        </div>
      )}

      {state === 'error' && (
        <div style={{ ...PANEL_STYLE, color: 'var(--v2-danger)' }}>
          Couldn&rsquo;t load that Trust Score. Try again in a moment.
        </div>
      )}

      {state === 'none' && (
        <div style={PANEL_STYLE}>
          <div style={{ fontSize: 'var(--v2-trust-title-size)',
            fontWeight: 'var(--v2-trust-title-weight)', marginBottom: 6 }}>
            Nothing published at this address
          </div>
          <p style={{ fontSize: 'var(--v2-trust-body-size)', color: 'var(--v2-text-secondary)',
            lineHeight: 1.6, margin: 0 }}>
            Either this address is wrong, or the agent&rsquo;s owner has not published its score.
            If someone sent you this link, ask them to publish it or to share the score directly.
          </p>
        </div>
      )}

      {state === 'ready' && score && (
        <div style={PANEL_STYLE}>
          <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
            <Dial score={score.score} style={style} label="Trust score" />
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 'var(--v2-trust-title-size)',
                fontWeight: 'var(--v2-trust-title-weight)', marginBottom: 6 }}>
                {score.subjectName}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
                {score.basisLabel && <Pill>{String(score.basisLabel).toUpperCase()}</Pill>}
                {evidenceLabel(score.rung) && <Pill>{evidenceLabel(score.rung)}</Pill>}
                {score.confidence != null && (
                  <Pill>CONFIDENCE {Number(score.confidence).toFixed(2)}</Pill>
                )}
              </div>
              <p style={{ fontSize: 'var(--v2-trust-body-size)',
                color: 'var(--v2-text-secondary)', lineHeight: 1.6, margin: 0 }}>
                {score.measures}
              </p>
            </div>
          </div>

          {deductions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SectionLabel>{deductionLabel}</SectionLabel>
              {deductions.map((d) => (
                <DeductionRow key={d.factorKey} name={factorName(d.factorKey)} points={d.points} />
              ))}
            </div>
          )}

          {factors.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SectionLabel>Factors</SectionLabel>
              {factors.map((f) => (
                <FactorRow key={f.factorKey} factor={f} nameOf={factorName} />
              ))}
            </div>
          )}

          <p style={{ fontSize: 'var(--v2-trust-meta-size)', color: 'var(--v2-text-muted)',
            lineHeight: 1.6, marginTop: 14, paddingTop: 10,
            borderTop: '1px solid var(--v2-border-subtle)' }}>
            Scored on the published method, version {score.methodologyVersion}. The score and its
            confidence are read together: confidence is how much of the evidence the method asks for
            actually exists, and a thin reading is not a clean bill of health.
          </p>
        </div>
      )}
    </div>
  );
}

export default TrustScoreAddressPage;
